<template>
  <!-- The pucks that matter, and nothing else. The modal this replaces drew a
       single stage that panned a camera between two shrunken pucks; in a 380 px
       dock there is no room for a camera and no reason for one, because the
       user is checking two facts — which well the box leaves and which well it
       lands in — and both are stated here without moving anything: a ring on
       the well it leaves, a heavy dashed ring with a translucent box in the
       well it lands in, and a line of words under each puck saying the same
       thing again for anyone who does not want to read rings. -->
  <div
    class="sa-voice-focus"
    :class="{ 'sa-voice-focus-pair': columns.length > 1 }"
  >
    <template v-for="(column, index) in columns" :key="column.key">
      <!-- Only ever between two columns, and only on the wide layout: on a
           phone the columns stack, where an arrow pointing right would be
           pointing at the edge of the sheet. -->
      <span v-if="index > 0" class="sa-voice-focus-arrow" aria-hidden="true"
        >→</span
      >
      <figure class="sa-voice-focus-col">
        <figcaption class="sa-voice-focus-head">
          <span v-if="column.role" class="sa-voice-focus-role">{{
            column.role
          }}</span>
          <!-- The same words the page behind prints for the same containers, so
               the user can check the command is pointed at what they are looking
               at without translating between two vocabularies. The parts are
               flex items so the line breaks BETWEEN them and never inside one:
               "VOICE1 › V / P1" is a serial the user cannot read back. -->
          <span class="sa-voice-focus-crumb">
            <template
              v-for="(part, i) in column.crumb"
              :key="column.key + '-crumb-' + i"
            >
              <span v-if="i > 0" class="sa-voice-focus-sep" aria-hidden="true"
                >›</span
              >
              <span
                class="sa-voice-focus-crumb-part"
                :class="{
                  'sa-voice-focus-crumb-leaf': part.leaf,
                  'sa-voice-focus-crumb-clip': part.clip,
                }"
                :title="part.clip ? part.text : null"
                >{{ part.text }}</span
              >
            </template>
          </span>
        </figcaption>

        <svg
          class="sa-voice-focus-puck"
          :class="{ 'sa-voice-focus-puck-unknown': !column.known }"
          viewBox="0 0 407.54 435.08"
          role="img"
          :aria-label="column.ariaLabel"
        >
          <!-- The app's own puck art on this exact canvas: it drops in at 0,0
               and carries the shading the drawn body has no gradient for. -->
          <image
            href="assets/images/silver-puck-fade.svg"
            x="0"
            y="0"
            width="407.54"
            height="435.08"
          />
          <!-- …and over it, the puck in its own colour, exactly as the rack
               card draws it: same paths, same `transformPuckColor` rule, so a
               gold puck in the dock is the gold puck on the grid. A puck the
               store could not find is left as the grey artwork, because a
               colour is a fact about a puck and we do not have one. -->
          <voice-puck-body
            v-if="column.known"
            :body-fill="column.fill.body"
            :cup-fill="column.fill.cup"
          />

          <g v-for="well in column.wells" :key="column.key + '-well-' + well.n">
            <!-- Occupied wells only, in the puck renderer's own order and with
                 the puck renderer's own components. An empty well is the
                 artwork's painted recess, exactly as it is on the puck behind;
                 the screen's `emptybox` placeholder is not reused because it
                 reads "Click to add a Box", which is neither legible at this size
                 nor true of anything in this dock. -->
            <clipped
              v-if="well.boxType == 'Clipped'"
              :x="well.x + 'px'"
              :y="well.y + 'px'"
              :width="slotWidth"
              :height="slotHeight"
              :show-grid-clips="well.gridClips"
              :well-number="well.n"
              class="well-clipped"
              :class="`well-${well.colorSlug}-clipped`"
            ></clipped>
            <unclipped
              v-else-if="well.boxType == 'Unclipped'"
              :x="well.x + 'px'"
              :y="well.y + 'px'"
              :width="slotWidth"
              :height="slotHeight"
              :show-grid-clips="well.gridClips"
              :well-number="well.n"
              class="well-unclipped"
              :class="`well-${well.colorSlug}-unclipped`"
            ></unclipped>
            <opencup
              v-else-if="well.boxType == 'opencup'"
              :x="well.x + 'px'"
              :y="well.y + 'px'"
              :width="slotWidth"
              :height="slotHeight"
              :show-grid-clips="well.gridClips"
              :well-number="well.n"
              class="well-unclipped"
              :class="`well-${well.colorSlug}-unclipped`"
            ></opencup>
          </g>

          <!-- Where the box is now. Red when the command would delete it, because
               the difference between "this one is moving" and "this one is going
               away" is the only thing the user has to get right before they
               confirm. -->
          <circle
            v-if="column.ring"
            class="sa-voice-focus-ring"
            :class="{ 'sa-voice-focus-ring-delete': column.ring.danger }"
            :cx="column.ring.at.x + slotWidth / 2"
            :cy="column.ring.at.y + slotHeight / 2"
            r="45"
          />

          <!-- Where it would go. The ring was dashed and empty on the theory
               that a box drawn in the destination is a picture of a change that
               has not happened; in a 190 px puck that theory produced a faint
               outline the product owner could not find at all. So the box IS
               drawn — the one that is actually moving, in its own colour — but
               at 45% and inside a dashed ring, which is how the rest of this
               app already spells "proposed, not applied". -->
          <g v-if="column.ghost">
            <!-- A white plate under the translucent box. Without it the box is
                 composited against the cup it is landing in, and a gold box in
                 a chrome puck comes out the same olive grey as an empty well —
                 which defeats the point of drawing the box's own colour. -->
            <circle
              class="sa-voice-focus-ghost-plate"
              :cx="column.ghost.at.x + slotWidth / 2"
              :cy="column.ghost.at.y + slotHeight / 2"
              r="40"
            />
            <g class="sa-voice-focus-ghost-glyph">
              <unclipped
                v-if="column.ghost.glyph.boxType == 'Unclipped'"
                :x="column.ghost.at.x + 'px'"
                :y="column.ghost.at.y + 'px'"
                :width="slotWidth"
                :height="slotHeight"
                :show-grid-clips="column.ghost.glyph.gridClips"
                :well-number="column.ghost.well"
                class="well-unclipped"
                :class="`well-${column.ghost.glyph.colorSlug}-unclipped`"
              ></unclipped>
              <opencup
                v-else-if="column.ghost.glyph.boxType == 'opencup'"
                :x="column.ghost.at.x + 'px'"
                :y="column.ghost.at.y + 'px'"
                :width="slotWidth"
                :height="slotHeight"
                :show-grid-clips="column.ghost.glyph.gridClips"
                :well-number="column.ghost.well"
                class="well-unclipped"
                :class="`well-${column.ghost.glyph.colorSlug}-unclipped`"
              ></opencup>
              <clipped
                v-else
                :x="column.ghost.at.x + 'px'"
                :y="column.ghost.at.y + 'px'"
                :width="slotWidth"
                :height="slotHeight"
                :show-grid-clips="column.ghost.glyph.gridClips"
                :well-number="column.ghost.well"
                class="well-clipped"
                :class="`well-${column.ghost.glyph.colorSlug}-clipped`"
              ></clipped>
            </g>
            <rect
              class="sa-voice-focus-ghost"
              :x="column.ghost.at.x + 2"
              :y="column.ghost.at.y + 2"
              :width="slotWidth - 4"
              :height="slotHeight - 4"
              rx="40"
            />
          </g>
        </svg>

        <!-- The sentence, under the picture. A ring is a claim the user has to
             decode against a numeral they can barely read at this size; "To
             well 5" is the same claim in the words they said. -->
        <p
          v-if="column.caption"
          class="sa-voice-focus-caption"
          :class="{ 'sa-voice-focus-caption-danger': column.captionDanger }"
        >
          {{ column.caption }}
        </p>

        <p class="sa-voice-focus-note">{{ column.note }}</p>

        <!-- Only for a puck that is actually IN a rack. "Show in rack" on an
             unknown puck would call startAutoNavigate and find nothing (review A,
             nit 9) — and so would one on a puck in the unassigned pool, which is
             known but which startAutoNavigate's rack walk never reaches. -->
        <button
          v-if="column.known && column.inRack"
          type="button"
          class="sa-voice-focus-action"
          @click="showInRack(column)"
        >
          Show in rack
        </button>
      </figure>
    </template>
  </div>
</template>

<script>
import {
  PUCK_WELL_SLOT_W,
  PUCK_WELL_SLOT_H,
  wellCaption,
} from "../../utils/voiceFocusModel.js";
import VoicePuckBody from "./VoicePuckBody.vue";
import clipped from "../router-pages/loggedin/manage-products-sections/clipped.vue";
import unclipped from "../router-pages/loggedin/manage-products-sections/unclipped.vue";
import opencup from "../router-pages/loggedin/manage-products-sections/opencup.vue";

// The greys of `assets/images/silver-puck-fade.svg`, the layer this body is
// drawn over. The template only draws a body for a puck the store found, so
// these are reached when the store itself has no `transformPuckColor` to ask:
// a body that then falls back to the artwork behind it looks like an
// uncoloured puck, where one with no fill at all looks like a hole in the art.
const UNKNOWN_BODY = "#9d9fa2";
const UNKNOWN_CUP = "#616264";

// A breadcrumb part longer than this is clipped with an ellipsis and keeps its
// full text in a `title`. Fourteen is the widest a middle segment can be before
// two of them stop fitting on one line of a 380 px dock; the leaf is exempt
// because the leaf is the serial, which is the one string the user is checking.
const CRUMB_CLIP_AT = 14;

export default {
  name: "VoiceFocusView",
  components: { VoicePuckBody, clipped, unclipped, opencup },
  props: {
    /** The object `voiceFocusModel.makeFocusModel` returns. */
    model: { type: Object, required: true },
  },
  emits: ["show-in-rack"],
  computed: {
    slotWidth() {
      return PUCK_WELL_SLOT_W;
    },
    slotHeight() {
      return PUCK_WELL_SLOT_H;
    },
    /**
     * One or two columns.
     *
     * The destination gets a column of its own only when the box crosses to
     * another puck. A same-puck move and a create both happen inside one puck,
     * and drawing that puck twice would ask the user to compare two pictures
     * that are the same picture.
     */
    columns() {
      const model = this.model || {};
      const cross = !!model.crossPuck;
      const caption = wellCaption(model);
      const ghost = model.destinationCoordinate
        ? {
            at: model.destinationCoordinate,
            well: model.destinationWell,
            glyph: model.glyph || {},
          }
        : null;
      const out = [
        this.buildColumn({
          key: "source",
          role: cross ? "From" : null,
          facet: model.source,
          wells: model.primaryWells,
          known: !!model.primaryKnown,
          inRack: !!model.primaryInRack,
          color: model.primaryColor,
          ring: model.sourceCoordinate
            ? {
                at: model.sourceCoordinate,
                danger: model.intent === "delete_box",
              }
            : null,
          // On a single-puck preview the destination well is drawn in the same
          // puck, which is where it actually is.
          ghost: cross ? null : ghost,
          caption: caption.source,
          captionDanger: caption.danger,
        }),
      ];
      if (cross && model.destination) {
        out.push(
          this.buildColumn({
            key: "destination",
            role: "To",
            facet: model.destination,
            wells: model.destinationWells,
            known: !!model.destinationKnown,
            inRack: !!model.destinationInRack,
            color: model.destinationColor,
            ring: null,
            ghost,
            caption: caption.destination,
            captionDanger: false,
          })
        );
      }
      return out;
    },
  },
  methods: {
    buildColumn(spec) {
      const facet = spec.facet || {};
      const wells = spec.wells || [];
      const label = facet.label || "Puck";
      // States what the drawing is showing, including when it is showing
      // nothing: a puck drawn with twelve empty wells because the dewar had not
      // loaded would say "this puck is empty", which is a claim about someone's
      // samples.
      const note = spec.known
        ? `${wells.filter((well) => well.filled).length} of 12 wells filled`
        : "Well contents not shown";
      const parts = (facet.breadcrumb || []).filter((part) => !!part);
      return {
        key: spec.key,
        role: spec.role,
        label,
        well: facet.well == null ? null : facet.well,
        crumb: parts.map((text, i) => {
          const leaf = i === parts.length - 1;
          return {
            text: String(text),
            leaf,
            clip: !leaf && String(text).length > CRUMB_CLIP_AT,
          };
        }),
        wells,
        known: spec.known,
        inRack: !!spec.inRack,
        fill: this.puckFill(spec.color, spec.known),
        ring: spec.ring,
        ghost: spec.ghost,
        caption: spec.caption || null,
        captionDanger: !!spec.captionDanger,
        note,
        ariaLabel: `Puck ${label}. ${note}.`,
      };
    },
    /**
     * The two fills the rack card gives the same puck.
     *
     * Through the store's own `transformPuckColor` and nothing else — the dock
     * must not own a second colour table. A located puck with no colour set
     * comes back black here for the same reason it comes back black on the
     * grid: that is what the shared rule returns for an unset colour, and the
     * two drawings have to agree.
     */
    puckFill(color, known) {
      const transform =
        this.$store && this.$store.state
          ? this.$store.state.transformPuckColor
          : null;
      if (!known || typeof transform !== "function") {
        return { body: UNKNOWN_BODY, cup: UNKNOWN_CUP };
      }
      return { body: transform(color), cup: transform(color, true) };
    },
    showInRack(column) {
      this.$emit("show-in-rack", {
        puckLabel: column.label,
        well: column.well,
      });
    },
  },
};
</script>

<style scoped>
/* Fills whatever the dock gives it. Two pucks share the width on a side panel
   and stack on a phone sheet, where a half-width puck is smaller than the wells
   it is trying to distinguish. */
.sa-voice-focus {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  margin: 0 0 0.875rem;
  padding: 0.5rem;
  border: 1px solid #0c4a6e;
  border-radius: 12px;
  background: #f0f9ff;
}
.sa-voice-focus-col {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
  margin: 0;
}
/* Sized off the puck, not off the text around it: it has to read as part of the
   drawing rather than as punctuation in the caption. */
.sa-voice-focus-arrow {
  align-self: center;
  flex: 0 0 auto;
  color: #0284c7;
  font-size: 22px;
  line-height: 1;
}
.sa-voice-focus-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: center;
  gap: 0.25rem;
  width: 100%;
}
.sa-voice-focus-role {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #0c4a6e;
}
/* Flex, so the only break opportunities are the gaps between the parts. The
   crumb used to be inline text with `overflow-wrap: anywhere`, which broke
   "VP1" across two lines in the middle of the serial. */
.sa-voice-focus-crumb {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: center;
  max-width: 100%;
  font-size: 12px;
  line-height: 1.3;
  color: #475569;
  text-align: center;
}
.sa-voice-focus-sep {
  margin: 0 0.25rem;
  color: #94a3b8;
}
/* A container name is never broken mid-word. If it does not fit it is clipped
   with an ellipsis and keeps its full text in a `title`; the leaf never is. */
.sa-voice-focus-crumb-part {
  white-space: nowrap;
}
.sa-voice-focus-crumb-clip {
  max-width: 12ch;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* The puck's own name carries the weight: the dewar and rack are there to be
   checked, the serial is there to be read. */
.sa-voice-focus-crumb-leaf {
  font-weight: 700;
  color: #0f172a;
}
.sa-voice-focus-puck {
  display: block;
  width: 100%;
  max-width: 190px;
  height: auto;
}
/* The dewar has not loaded, so which wells hold boxes is not known. Nothing is
   drawn into the wells at all, and the whole drawing steps back to read as a
   diagram rather than as a reading. The note beneath says so in words, which is
   the part that actually carries it. */
.sa-voice-focus-puck-unknown {
  opacity: 0.55;
}
/* Stroke only. A translucent fill over a well tints whatever box is in it, and
   the box's colour is one of the things the user is checking. */
.sa-voice-focus-ring {
  fill: none;
  stroke: #0284c7;
  stroke-width: 6;
}
.sa-voice-focus-ring-delete {
  stroke: #dc2626;
  stroke-width: 8;
}
/* Heavier than the source ring and dashed: the destination is the half of the
   sentence the user has not seen yet, so it is the half that has to be found
   first. At 190 px the old 5 px hairline rendered as about one screen pixel. */
.sa-voice-focus-ghost {
  fill: none;
  stroke: #0284c7;
  stroke-width: 7;
  stroke-dasharray: 12 9;
  pointer-events: none;
}
/* Proposed, not applied. Solid enough to read the box's colour and type off,
   faint enough that nobody mistakes it for a box that is already there. */
.sa-voice-focus-ghost-glyph {
  opacity: 0.45;
  pointer-events: none;
}
.sa-voice-focus-ghost-plate {
  fill: #ffffff;
  opacity: 0.85;
  pointer-events: none;
}
/* Darker than the ring it describes: a 6 px stroke passes contrast at 3:1, a
   12 px word does not. */
.sa-voice-focus-caption {
  margin: 0;
  color: #0369a1;
  font-size: 12px;
  font-weight: 700;
  text-align: center;
}
.sa-voice-focus-caption-danger {
  color: #b91c1c;
}
.sa-voice-focus-note {
  margin: 0;
  color: #475569;
  font-size: 12px;
  text-align: center;
}
.sa-voice-focus-action {
  margin: 0.125rem 0 0;
  padding: 0.25rem 0.625rem;
  border: 1px solid #0c4a6e;
  border-radius: 999px;
  background: #ffffff;
  color: #0c4a6e;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.sa-voice-focus-action:hover {
  background: #e0f2fe;
}
.sa-voice-focus-action:focus-visible {
  outline: 2px solid #0284c7;
  outline-offset: 2px;
}

@media (max-width: 479px) {
  .sa-voice-focus-pair {
    flex-direction: column;
    align-items: stretch;
  }
  .sa-voice-focus-puck {
    max-width: 240px;
  }
  /* The columns are stacked now, so a rightward arrow points at nothing. The
     two captions still say which way the box is going. */
  .sa-voice-focus-arrow {
    display: none;
  }
}
</style>
